import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, FileText, Eye, Download, Filter, ArrowLeft, Trash2, RefreshCw } from 'lucide-react';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import { Input } from '@shared/components/ui/Input';
import { Button } from '@shared/components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';

interface Report {
  id: string;
  user_id: string;
  title: string | null;
  target_role: string | null;
  industry: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  is_rerun?: boolean;
  consumed_credit?: boolean;
  profiles?: {
    full_name: string;
  };
}

const AdminReports: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reports, setReports] = useState<Report[]>([]);
  const [filteredReports, setFilteredReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [rerunningReportId, setRerunningReportId] = useState<string | null>(null);

  useEffect(() => {
    loadReports();
  }, []);

  useEffect(() => {
    let filtered = reports;

    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(
        (report) =>
          (report.profiles?.full_name && report.profiles.full_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (report.title && report.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (report.target_role && report.target_role.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (report.industry && report.industry.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((report) => report.status === statusFilter);
    }

    setFilteredReports(filtered);
  }, [searchTerm, statusFilter, reports]);

  const loadReports = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('reports')
        .select(`
          *,
          profiles (
            full_name
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setReports(data || []);
      setFilteredReports(data || []);
    } catch (error: any) {
      console.error('Error loading reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReport = async (reportId: string, reportTitle: string) => {
    if (!confirm(`Are you sure you want to delete the report "${reportTitle}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      console.log('Admin deleting report:', reportId);

      const { error } = await supabase
        .from('reports')
        .delete()
        .eq('id', reportId);

      if (error) throw error;

      toast.success('Report deleted successfully');
      loadReports();
    } catch (error: any) {
      console.error('Error deleting report:', error);
      toast.error(`Failed to delete report: ${error.message}`);
    }
  };

  const handleRerunReport = async (report: Report) => {
    if (!user) {
      toast.error('You must be logged in to re-run reports');
      return;
    }

    const confirmMessage = `Re-run report for ${report.profiles?.full_name || 'user'}?\n\n` +
      `This will generate a new report without consuming a usage credit.\n` +
      `Original report: ${report.title}\n\n` +
      `Continue?`;

    if (!confirm(confirmMessage)) {
      return;
    }

    setRerunningReportId(report.id);

    try {
      console.log('Admin re-running report:', report.id, 'for user:', report.user_id);

      const { data, error } = await supabase.functions.invoke('generate-report', {
        body: {
          userId: report.user_id,
          reportTitle: report.title || `${report.profiles?.full_name || 'User'} Career Intelligence Report - Re-run`,
          isRerun: true,
          originalReportId: report.id,
          rerunByAdminId: user.id,
          rerunReason: 'Admin initiated re-run'
        }
      });

      if (error) {
        throw new Error(`Failed to initiate re-run: ${error.message}`);
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Re-run failed');
      }

      toast.success(`Report re-run initiated successfully! The new report will appear shortly.`);

      setTimeout(() => {
        loadReports();
      }, 2000);
    } catch (error: any) {
      console.error('Error re-running report:', error);
      toast.error(`Failed to re-run report: ${error.message}`);
    } finally {
      setRerunningReportId(null);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
      case 'generating':
        return 'bg-blue-100 text-blue-800';
      case 'failed':
      case 'error':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Reports Management</h1>
        <p className="text-gray-600">View and manage all generated reports</p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search by user, title, role, or industry..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="processing">Processing</option>
              <option value="generating">Generating</option>
              <option value="failed">Failed</option>
              <option value="error">Error</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title / Target Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Updated
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredReports.map((report) => (
                <tr key={report.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {report.profiles?.full_name || 'Unknown User'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{report.title || report.target_role || '-'}</div>
                    {report.industry && (
                      <div className="text-xs text-gray-500">{report.industry}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(
                          report.status
                        )}`}
                      >
                        {report.status}
                      </span>
                      {report.is_rerun && (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                          Re-run
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(report.created_at).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {report.updated_at
                      ? new Date(report.updated_at).toLocaleString()
                      : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end items-center gap-2">
                      {report.status === 'completed' && (
                        <Link
                          to={`/reports/${report.id}`}
                          className="text-primary hover:text-primary/80 inline-flex items-center gap-1"
                          title="View report"
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                      )}
                      {(report.status === 'failed' || report.status === 'error' || report.status === 'completed') && (
                        <button
                          onClick={() => handleRerunReport(report)}
                          disabled={rerunningReportId === report.id}
                          className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Re-run report without consuming credit"
                        >
                          <RefreshCw className={`w-4 h-4 ${rerunningReportId === report.id ? 'animate-spin' : ''}`} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteReport(report.id, report.title || 'Untitled Report')}
                        className="text-red-600 hover:text-red-800 inline-flex items-center gap-1"
                        title="Delete report"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredReports.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No reports found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminReports;
